// One geocode query in flight, app-wide.
//
// A debounce on a search field stops a query per keystroke, but on its own
// it does not stop a *second* query from starting while the first is still
// reading. Discarding a superseded answer is not the same as never asking
// for it: one query against the planet index is roughly forty HTTP byte-range
// requests taking several seconds, and typing an address with natural pauses
// stacked those queries several deep until the browser refused to open new
// connections (ERR_INSUFFICIENT_RESOURCES) and the field died.
//
// So the queue has depth one. While a query runs we remember only the newest
// request, because the intermediate ones are answers nobody will ever read.
// A request superseded before it runs resolves to `null` rather than to an
// empty list — the caller holds the last real answer on screen instead of
// blanking it, and waits for the better one that is already coming.
//
// This is deliberately not a per-component concern. Two search fields mounted
// at once would otherwise each keep one query in flight, and the bound that
// matters is on the browser's connection pool, which is shared.

export type GeocodeRun<T> = () => Promise<T>;

export interface GeocodeQueue<T> {
  /**
   * Run `job`, unless a later call replaces it first. Resolves with the
   * result, or `null` if this request was superseded before it ran.
   */
  submit(job: GeocodeRun<T>): Promise<T | null>;
  /** In-flight count. Never exceeds 1; exposed so tests can assert that. */
  readonly running: boolean;
}

export function createGeocodeQueue<T>(): GeocodeQueue<T> {
  type Pending = {
    job: GeocodeRun<T>;
    resolve(value: T | null): void;
    reject(error: unknown): void;
  };

  let running = false;
  let queued: Pending | null = null;

  async function pump(): Promise<void> {
    if (running) return;
    running = true;
    try {
      while (queued) {
        const next = queued;
        queued = null;
        try {
          next.resolve(await next.job());
        } catch (error) {
          // A failing lookup rejects, and must never be reported as `null`:
          // the caller reads `null` as "a better answer is on its way" and
          // would keep the field spinning forever on an error.
          next.reject(error);
        }
      }
    } finally {
      running = false;
    }
  }

  return {
    submit(job) {
      return new Promise<T | null>((resolve, reject) => {
        // Whatever was waiting its turn is now known to be stale: nobody
        // wants the older text once newer text exists.
        queued?.resolve(null);
        queued = { job, resolve, reject };
        void pump();
      });
    },
    get running() {
      return running;
    }
  };
}
